import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  GraduationCap, CheckCircle2, ArrowRight, Sparkles, MonitorSmartphone,
  BarChart3, Users, ShieldCheck, Award, BookOpen, Video, FileCheck, Layers,
  ChevronRight, ChevronDown, Play, Server, Clock, Lock, Smartphone, Globe, ClipboardCheck,
  CreditCard, MessageSquare, HelpCircle, Phone, ShieldAlert, KeyRound, HardDrive, Zap, RefreshCw,
  Rocket, TrendingUp, Crown, Building2, User, PhoneCall, ArrowLeft
} from "lucide-react";
import { Reveal, SectionLabel, Stagger, StaggerItem, Highlight } from "@/components/motion";
import CTABanner from "@/components/CTABanner";

// Bespoke illustrated feature icons matching reference style
const AppIcon = () => (
  <svg viewBox="0 0 64 64" className="w-16 h-16 sm:w-20 sm:h-20 drop-shadow-sm" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="17" y="5" width="30" height="54" rx="7" fill="#00AEEF" />
    <line x1="28" y1="10" x2="36" y2="10" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
    <polygon points="26,23 41,32 26,41" fill="none" stroke="white" strokeWidth="2" strokeLinejoin="round" />
    <line x1="26" y1="23" x2="36" y2="32" stroke="white" strokeWidth="1.6" />
    <line x1="26" y1="41" x2="36" y2="32" stroke="white" strokeWidth="1.6" />
    <line x1="26" y1="23" x2="31" y2="39" stroke="white" strokeWidth="1.6" />
    <circle cx="32" cy="53" r="1.5" fill="white" />
  </svg>
);

const WebsiteIcon = () => (
  <svg viewBox="0 0 64 64" className="w-16 h-16 sm:w-20 sm:h-20 drop-shadow-sm" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="32" cy="32" r="22" fill="#00AEEF" />
    <ellipse cx="32" cy="32" rx="11" ry="22" stroke="white" strokeWidth="1.5" fill="none" />
    <line x1="10" y1="32" x2="54" y2="32" stroke="white" strokeWidth="1.8" />
    <path d="M14 21 C 22 25, 42 25, 50 21" stroke="white" strokeWidth="1.5" fill="none" />
    <path d="M14 43 C 22 39, 42 39, 50 43" stroke="white" strokeWidth="1.5" fill="none" />
    <rect x="13" y="24.5" width="38" height="15" rx="3" fill="#00AEEF" stroke="white" strokeWidth="1.5" />
    <text x="32" y="35.5" fill="white" fontSize="10.5" fontWeight="800" textAnchor="middle" fontFamily="system-ui, -apple-system, sans-serif" letterSpacing="0.8">WWW</text>
  </svg>
);

const CoursesIcon = () => (
  <svg viewBox="0 0 64 64" className="w-16 h-16 sm:w-20 sm:h-20 drop-shadow-sm" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="15" y="12" width="34" height="12" rx="3.5" fill="#00AEEF" />
    <path d="M43 14.5 H47 V21.5 H43 Z" fill="white" />
    <path d="M24 12 V20 L27 17.5 L30 20 V12 Z" fill="white" />
    <rect x="15" y="26" width="34" height="12" rx="3.5" fill="#00AEEF" />
    <path d="M43 28.5 H47 V35.5 H43 Z" fill="white" />
    <line x1="19" y1="32" x2="35" y2="32" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
    <rect x="15" y="40" width="34" height="12" rx="3.5" fill="#00AEEF" />
    <path d="M43 42.5 H47 V49.5 H43 Z" fill="white" />
    <line x1="19" y1="46" x2="35" y2="46" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const LiveClassesIcon = () => (
  <svg viewBox="0 0 64 64" className="w-16 h-16 sm:w-20 sm:h-20 drop-shadow-sm" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="12" y="19" width="30" height="26" rx="8" fill="#00AEEF" />
    <path d="M43 27 L54 20 V44 L43 37 Z" fill="#00AEEF" />
    <path d="M21 28 C 24 24, 30 24, 33 28" stroke="white" strokeWidth="2.2" strokeLinecap="round" fill="none" />
    <path d="M24 32 C 25.5 30, 28.5 30, 30 32" stroke="white" strokeWidth="2.2" strokeLinecap="round" fill="none" />
    <circle cx="27" cy="37" r="1.8" fill="white" />
  </svg>
);

const OnlineTestsIcon = () => (
  <svg viewBox="0 0 64 64" className="w-16 h-16 sm:w-20 sm:h-20 drop-shadow-sm" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M27 9 C 27 7, 37 7, 37 9" stroke="#00AEEF" strokeWidth="2.2" fill="none" strokeLinecap="round" />
    <rect x="25" y="9" width="14" height="6" rx="2" fill="#00AEEF" />
    <rect x="16" y="12" width="32" height="44" rx="6" fill="#00AEEF" />
    <circle cx="32" cy="35" r="9" fill="white" />
    <path d="M28 35 L31 38 L36 32" stroke="#00AEEF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <line x1="24" y1="21" x2="40" y2="21" stroke="white" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const SecurityIcon = () => (
  <svg viewBox="0 0 64 64" className="w-16 h-16 sm:w-20 sm:h-20 drop-shadow-sm" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M32 7 L49 13.5 V28 C 49 40.5, 41.5 50.5, 32 55 C 22.5 50.5, 15 40.5, 15 28 V13.5 L32 7 Z" fill="#00AEEF" />
    <path d="M25 31 L30 36 L39 26" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

const BEST_FEATURES = [
  {
    Icon: AppIcon,
    title: "Own Branded App",
    line1: "Own Branded",
    line2: "App",
  },
  {
    Icon: WebsiteIcon,
    title: "Own Branded Website",
    line1: "Own Branded",
    line2: "Website",
  },
  {
    Icon: CoursesIcon,
    title: "Sell Your Courses",
    line1: "Sell Your",
    line2: "Courses",
  },
  {
    Icon: LiveClassesIcon,
    title: "Take Live Classes",
    line1: "Take Live",
    line2: "Classes",
  },
  {
    Icon: OnlineTestsIcon,
    title: "Create Online Tests",
    line1: "Create Online",
    line2: "Tests",
  },
  {
    Icon: SecurityIcon,
    title: "100% Content Security",
    line1: "100% Content",
    line2: "Security",
  },
];

const LIFETIME_PRICING_PLANS = [
  {
    id: "launch",
    name: "Launch Plan",
    Icon: Rocket,
    badge: "Solo Teachers",
    tagline: "Pay Once. Own your complete teaching website & mobile app for life.",
    price: 39999,
    priceLabel: "One-Time Setup · Lifetime App & Website Ownership",
    popular: false,
    appsIncluded: [
      { Icon: Globe, label: "Your Own Branded Website" },
      { Icon: Smartphone, label: "Your Own Android App (with your logo)" },
    ],
    coreFeatures: [
      "Sell Video Courses & PDF Study Material",
      "100% Anti-Piracy (No screen recording or downloads)",
      "Conduct Live Classes with Zoom & Jitsi",
      "Create Online Tests & Quizzes with Auto-Checking",
      "Student Login & Learning Dashboard",
      "Direct Payments to Your Bank (0% Commission)",
    ],
    year1Bonuses: [
      "1 Year Free Website & App Server Hosting",
      "50 GB Video Storage + 250 GB Video Streaming / Month",
    ],
    support: "Helpdesk Ticket Support (Reply within 48 hours)",
    cta: "Get Launch Plan",
  },
  {
    id: "pro",
    name: "Pro Scale Plan",
    Icon: TrendingUp,
    badge: "Growing Academies",
    tagline: "Pay Once. Advanced marketing and automation for growing academies.",
    price: 59999,
    priceLabel: "One-Time Setup · Lifetime App & Website Ownership",
    popular: true,
    appsIncluded: [
      { Icon: Globe, label: "Custom Designed Academy Website" },
      { Icon: Smartphone, label: "Branded Android App + Custom Splash Screen" },
    ],
    extraIncluded: "Includes Everything in Launch, PLUS:",
    coreFeatures: [
      "WhatsApp & Email Alerts for students (via third-party API)",
      "Automatic Course Completion Certificates with student name",
      "Student Referral & Affiliate Marketing System",
      "Multi-Teacher Scheduling & Batch Management",
      "Create Course Bundles & Special Discount Offers",
    ],
    year1Bonuses: [
      "1 Year Free Website & App Server Hosting",
      "120 GB Video Storage + 600 GB Video Streaming / Month",
    ],
    support: "Priority WhatsApp Group Support (Reply within 12 hours)",
    consultation: "1-on-1 App Launch & Marketing Strategy Call (1 Hour)",
    cta: "Get Pro Scale Plan",
  },
  {
    id: "enterprise",
    name: "Enterprise VIP",
    Icon: Crown,
    badge: "Institutes & Schools",
    tagline: "Pay Once. Full custom feature coding & multi-branch management.",
    price: 79999,
    priceLabel: "One-Time Setup · Lifetime App & Website Ownership",
    popular: false,
    appsIncluded: [
      { Icon: Globe, label: "Premium Custom Web Portal" },
      { Icon: Smartphone, label: "Dual Mobile App (Android App + iOS Web App)" },
    ],
    extraIncluded: "Includes Everything in Pro Scale, PLUS:",
    coreFeatures: [
      "Up to 15 Hours of Custom Feature Coding by our developers",
      "Multi-Branch & Multi-Center Academy Management",
      "BigBlueButton & High-Capacity Live Lecture Server",
      "Bulk Student Enrolments & Branch-Wise Reports",
    ],
    year1Bonuses: [
      "1 Year Free High-Speed Server Hosting",
      "250 GB Video Storage + 1,500 GB Video Streaming / Month",
    ],
    support: "24/7 Dedicated Emergency Phone Hotline",
    consultation: "3 Months Marketing & Growth Advisory (Bi-weekly calls)",
    cta: "Get Enterprise VIP Plan",
  },
];

const USAGE_COSTS = [
  {
    image: "/usage-video.jpg",
    title: "Extra Video Streaming",
    badge: "Only if you exceed Year 1 limit",
    highlight: "₹50 per extra 100 GB",
    desc: "Your app will never stop working if student traffic surges. If you ever use more than your included monthly video bandwidth in Year 1, extra streaming is billed transparently at just ₹50 per 100 GB.",
  },
  {
    image: "/usage-payment.jpg",
    title: "Direct Student Payments",
    badge: "0% Platform Cut",
    highlight: "Money goes straight to your bank",
    desc: "Connect your own payment gateway (Razorpay, Paytm, or Cashfree) to receive student fee payments directly in your bank account. Standard gateway fee applies (approx. 1.9% to 2%). We take 0% commission.",
  },
  {
    image: "/usage-messaging.jpg",
    title: "Email, SMS & WhatsApp",
    badge: "Free Software Dashboards",
    highlight: "Pay only for carrier API credits",
    desc: "All software tools to send emails, SMS, and WhatsApp notifications are 100% free for life. You only pay the actual service provider if you send SMS (₹0.28 / SMS) or WhatsApp alerts (₹0.55 / message).",
  },
];

const LMS_FAQS = [
  {
    q: "How does the 'Lifetime Ownership' license work? Do I really pay only once?",
    a: "Yes, 100%! Unlike subscription platforms (like Classplus or Graphy) that charge ₹20,000 to ₹60,000 every single year, our model is a one-time setup fee. You own the software license for your custom website and Android app for life with zero monthly platform subscriptions.",
  },
  {
    q: "Are there any hidden commissions or revenue cuts on my course sales?",
    a: "Zero. We take 0% commission on your course sales, mock tests, and student fees. Payments go directly into your own bank account through your preferred payment gateway (Razorpay, Paytm, Cashfree, Stripe, etc.).",
  },
  {
    q: "How does content anti-piracy and video security work?",
    a: "Your content is protected with multi-layered anti-piracy security: dynamic student watermarks (displaying student name & phone number on screen), encrypted HLS video streaming, automated screen recording blocking, and download prevention.",
  },
  {
    q: "What happens after the first year of free hosting?",
    a: "In Year 1, all website hosting, app infrastructure, and video cloud streaming are 100% free. From Year 2 onward, you own the platform and only pay direct third-party hosting bills (e.g. your own Hostinger server at ~₹3,000/yr and actual video bandwidth on Bunny.net at ~₹10/100 watch hours). You never pay recurring software fees to us.",
  },
  {
    q: "Will the Android app have my own academy logo and brand name?",
    a: "Yes, completely. The app is 100% white-labeled with your academy name, custom logo, splash screen, and brand colors. Your students will only see your brand.",
  },
  {
    q: "Can I host live interactive classes and conduct online tests?",
    a: "Yes. You can host live interactive classes directly with native Zoom and Jitsi integration, schedule batch timings, and create online tests/quizzes with auto-checking, instant student ranking, and PDF answer keys.",
  },
  {
    q: "How long does it take to launch my website and mobile app?",
    a: "Typically 3 to 7 business days from the day you provide your branding assets (logo, brand colors, course list). Our engineering team handles complete setup, server configuration, and testing.",
  },
  {
    q: "What kind of technical support do I receive?",
    a: "You receive dedicated onboarding and technical assistance. Depending on your plan, you receive ticketing helpdesk, dedicated WhatsApp group support, or a 24/7 priority emergency line, plus a 1-on-1 launch strategy consultation.",
  },
];

export default function ProductLMS() {
  const [wizardStep, setWizardStep] = useState(1);
  const [selectedRole, setSelectedRole] = useState("coaching");
  const [leadData, setLeadData] = useState({ name: "", phone: "" });
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  const roles = [
    {
      id: "coaching",
      label: "I run a coaching centre",
      badge: "Coaching & Institutes",
      benefit: "Launch branded Android & iOS apps with batch management, live sessions & automated fee collection.",
      cta: "Launch Coaching App",
    },
    {
      id: "youtuber",
      label: "I am a YouTuber",
      badge: "Content Creators",
      benefit: "Monetize your audience with DRM-protected video courses, community hubs & digital products.",
      cta: "Monetize Your Audience",
    },
    {
      id: "school",
      label: "I teach in a school",
      badge: "Schools & Academies",
      benefit: "Modernize classrooms with hybrid learning paths, automated grading & parental communication.",
      cta: "Modernize Your School",
    },
    {
      id: "student",
      label: "I am an independent teacher",
      badge: "Independent Educators",
      benefit: "Access premier interactive courses, cohort discussions & verifiable certifications.",
      cta: "Explore Learning Hub",
    },
  ];

  const currentRole = roles.find((r) => r.id === selectedRole) || roles[0];

  const handleLeadSubmit = (e) => {
    e.preventDefault();
    if (!leadData.name.trim()) {
      toast.error("Please enter your full name");
      return;
    }
    if (!leadData.phone.trim() || leadData.phone.trim().length < 10) {
      toast.error("Please enter a valid 10-digit WhatsApp/phone number");
      return;
    }

    setIsSubmittingLead(true);
    setTimeout(() => {
      setIsSubmittingLead(false);
      toast.success("Demo request received! Our team will contact you shortly.");
      setWizardStep(3);
    }, 600);
  };

  return (
    <div data-testid="product-lms-page">
      {/* HERO */}
      <section
        className="relative pt-32 sm:pt-36 lg:pt-40 pb-20 lg:pb-24 px-5 sm:px-8 overflow-hidden bg-[#01193D] text-white"
        data-testid="lms-hero-section"
      >
        {/* Ambient Backdrops & Glowing Orbs */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#01112B] via-[#01193D] to-[#02204D] pointer-events-none" />
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[700px] h-[500px] bg-[#09B2ED]/10 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute -top-32 right-10 w-[450px] h-[450px] bg-[#2563EB]/20 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute inset-0 dot-grid-light opacity-30 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            
            {/* Left Column: Brand Headline & Value Prop & Stats */}
            <div className="lg:col-span-6 xl:col-span-7 flex flex-col justify-center text-left">
              <Reveal>
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#09B2ED]/10 border border-[#09B2ED]/30 text-[#91E0F1] text-xs font-semibold tracking-wide uppercase font-mono-brand mb-6 backdrop-blur-sm">
                  <Sparkles size={14} className="text-[#09B2ED] animate-pulse" />
                  <span>The Aurge Labs · LMS Platform</span>
                </div>

                <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-[52px] xl:text-[58px] tracking-tight text-white leading-[1.12]">
                  Launch your branded academy app &amp;{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#91E0F1] via-[#09B2ED] to-[#60A5FA]">
                    teaching website
                  </span>
                </h1>

                <p className="text-slate-300 text-base sm:text-lg lg:text-xl font-normal leading-relaxed mt-5 max-w-xl">
                  Pay once and own your complete teaching software for life. Sell encrypted video courses, conduct live classes, and collect student fees directly with 0% platform commission.
                </p>

                {/* Value Guarantee Metrics Bar */}
                <div className="mt-10 pt-8 border-t border-white/10 grid grid-cols-3 gap-4 sm:gap-6 max-w-xl">
                  <div className="pr-4 border-r border-white/15">
                    <div className="font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl text-white tracking-tight">
                      0%
                    </div>
                    <div className="text-xs sm:text-sm font-medium text-slate-300 mt-1">
                      Platform Cut
                    </div>
                    <div className="text-[11px] text-[#91E0F1] font-mono mt-0.5">
                      Direct Payouts
                    </div>
                  </div>
                  <div className="px-2 sm:px-4 border-r border-white/15">
                    <div className="font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl text-white tracking-tight">
                      100%
                    </div>
                    <div className="text-xs sm:text-sm font-medium text-slate-300 mt-1">
                      Anti-Piracy
                    </div>
                    <div className="text-[11px] text-[#91E0F1] font-mono mt-0.5">
                      Encrypted DRM
                    </div>
                  </div>
                  <div className="pl-2 sm:pl-4">
                    <div className="font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl text-white tracking-tight">
                      Lifetime
                    </div>
                    <div className="text-xs sm:text-sm font-medium text-slate-300 mt-1">
                      Ownership
                    </div>
                    <div className="text-[11px] text-[#91E0F1] font-mono mt-0.5">
                      Pay Once
                    </div>
                  </div>
                </div>

                {/* Primary CTA Buttons */}
                <div className="flex flex-wrap items-center gap-4 mt-9">
                  <Link
                    to="/contact"
                    data-testid="lms-hero-demo-cta"
                    className="btn-gradient text-white text-sm font-semibold px-7 py-3.5 rounded-full inline-flex items-center gap-2 shadow-lg shadow-blue-500/25"
                  >
                    Schedule Live LMS Demo <ArrowRight size={16} />
                  </Link>
                  <a
                    href="#pricing"
                    data-testid="lms-hero-explore-cta"
                    className="px-6 py-3.5 rounded-full border border-white/20 text-white hover:bg-white/10 text-sm font-semibold transition-all duration-200 inline-flex items-center gap-1.5 backdrop-blur-sm"
                  >
                    Explore Pricing & Plans <ChevronRight size={15} />
                  </a>
                </div>
              </Reveal>
            </div>

            {/* Right Column: Hero Visual & Interactive Persona Selector */}
            <div className="lg:col-span-6 xl:col-span-5 relative flex flex-col items-center">
              <Reveal delay={0.2} className="w-full relative">
                
                {/* Visual Backdrop Halo & Mentor Avatar */}
                <div className="relative mx-auto flex flex-col items-center">
                  
                  {/* Glowing Pedestal Backdrop Circle */}
                  <div className="relative w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96 rounded-full p-2 bg-gradient-to-b from-[#09B2ED]/30 via-[#2563EB]/20 to-transparent flex items-center justify-center">
                    <div className="absolute inset-0 rounded-full bg-[#09B2ED]/20 blur-2xl animate-pulse" />
                    
                    <div className="relative w-full h-full rounded-full overflow-hidden border-2 border-[#91E0F1]/30 shadow-2xl bg-[#02183D]">
                      <img
                        src="/lms-hero-mentor.jpg"
                        alt="Online Educator Mentor"
                        className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700"
                        onError={(e) => {
                          e.currentTarget.src = "/tech_skyline.png";
                        }}
                      />
                      {/* Gradient bottom mask for smooth blending */}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#01193D] via-transparent to-transparent opacity-80" />
                    </div>

                    {/* Floating Micro Feature Badges */}
                    <div className="absolute -left-3 top-10 sm:-left-6 bg-[#01193D]/90 border border-[#09B2ED]/40 backdrop-blur-md px-3.5 py-1.5 rounded-full text-[11px] font-medium text-[#91E0F1] shadow-lg flex items-center gap-1.5 animate-float">
                      <Sparkles size={13} className="text-[#09B2ED]" />
                      <span>100% White-Labeled</span>
                    </div>

                    <div className="absolute -right-3 bottom-12 sm:-right-4 bg-[#01193D]/90 border border-white/20 backdrop-blur-md px-3.5 py-1.5 rounded-full text-[11px] font-medium text-white shadow-lg flex items-center gap-1.5 animate-float-slow">
                      <ShieldCheck size={13} className="text-emerald-400" />
                      <span>Encrypted DRM</span>
                    </div>
                  </div>

                  {/* Multi-Step Lead Capture Wizard Box */}
                  <div className="relative -mt-6 sm:-mt-8 z-10 max-w-md w-full" data-testid="lms-hero-lead-wizard">
                    
                    {/* Speech Bubble Header */}
                    <div className="relative bg-[#09B2ED] text-[#01193D] font-display font-bold text-xs sm:text-sm px-5 py-3 rounded-2xl shadow-xl text-center flex items-center justify-between">
                      <span className="w-full text-center">
                        {wizardStep === 1 && "Step 1 of 2: Choose what best defines you"}
                        {wizardStep === 2 && "Step 2 of 2: Where should we send your demo?"}
                        {wizardStep === 3 && "🎉 Demo Access Request Confirmed!"}
                      </span>
                      {/* Speech Bubble Arrow pointing to mentor */}
                      <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-[#09B2ED] rotate-45 rounded-sm" />
                    </div>

                    {/* Step 1: Role Selection */}
                    {wizardStep === 1 && (
                      <motion.div
                        key="step-1"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-2.5 mt-3.5"
                      >
                        <div className="space-y-2">
                          {roles.map((role) => {
                            const isSelected = selectedRole === role.id;
                            return (
                              <button
                                key={role.id}
                                type="button"
                                onClick={() => {
                                  setSelectedRole(role.id);
                                  setWizardStep(2);
                                }}
                                data-testid={`role-selector-${role.id}`}
                                className={`w-full text-left px-4 py-3 rounded-xl border text-sm font-medium transition-all duration-200 flex items-center justify-between group ${
                                  isSelected
                                    ? "bg-gradient-to-r from-[#09B2ED]/25 to-[#2563EB]/25 border-[#09B2ED] text-white shadow-md ring-1 ring-[#09B2ED]"
                                    : "bg-[#011432]/85 border-white/15 text-slate-200 hover:border-[#09B2ED]/60 hover:bg-[#021f4d] hover:text-white"
                                }`}
                              >
                                <span className="flex items-center gap-3">
                                  <span
                                    className={`w-2.5 h-2.5 rounded-full transition-all ${
                                      isSelected ? "bg-[#09B2ED] ring-4 ring-[#09B2ED]/30" : "bg-white/30 group-hover:bg-[#09B2ED]"
                                    }`}
                                  />
                                  <span className="font-semibold tracking-wide text-xs sm:text-sm">{role.label}</span>
                                </span>
                                <ChevronRight
                                  size={16}
                                  className="text-[#09B2ED] group-hover:translate-x-1 transition-transform shrink-0"
                                />
                              </button>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}

                    {/* Step 2: Name & Phone Number Lead Capture */}
                    {wizardStep === 2 && (
                      <motion.div
                        key="step-2"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-3.5 p-4 sm:p-5 rounded-2xl bg-[#011432]/95 border border-[#09B2ED]/40 backdrop-blur-md shadow-2xl"
                      >
                        <div className="flex items-center justify-between pb-3 border-b border-white/10 mb-3.5 text-xs">
                          <button
                            type="button"
                            onClick={() => setWizardStep(1)}
                            className="text-slate-400 hover:text-white inline-flex items-center gap-1 font-medium transition-colors"
                          >
                            <ArrowLeft size={13} /> Back
                          </button>
                          <span className="font-mono text-[#91E0F1] font-semibold uppercase tracking-wider text-[11px]">
                            ✦ {currentRole.badge}
                          </span>
                        </div>

                        <form onSubmit={handleLeadSubmit} className="space-y-3">
                          <div>
                            <label className="block text-[11px] font-medium text-slate-300 mb-1">
                              Your Full Name
                            </label>
                            <div className="relative">
                              <User size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                              <input
                                type="text"
                                required
                                value={leadData.name}
                                onChange={(e) => setLeadData({ ...leadData, name: e.target.value })}
                                placeholder="e.g. Ramesh Sharma"
                                className="w-full bg-white/10 border border-white/20 rounded-xl pl-9 pr-3.5 py-2.5 text-xs sm:text-sm text-white placeholder:text-slate-400 focus:outline-none focus:border-[#09B2ED] focus:ring-2 focus:ring-[#09B2ED]/20 transition-all"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-[11px] font-medium text-slate-300 mb-1">
                              WhatsApp / Phone Number
                            </label>
                            <div className="relative">
                              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-[#09B2ED] font-mono">
                                +91
                              </span>
                              <input
                                type="tel"
                                required
                                maxLength={10}
                                value={leadData.phone}
                                onChange={(e) => {
                                  const val = e.target.value.replace(/\D/g, "");
                                  setLeadData({ ...leadData, phone: val });
                                }}
                                placeholder="98765 43210"
                                className="w-full bg-white/10 border border-white/20 rounded-xl pl-12 pr-3.5 py-2.5 text-xs sm:text-sm text-white placeholder:text-slate-400 focus:outline-none focus:border-[#09B2ED] focus:ring-2 focus:ring-[#09B2ED]/20 transition-all font-mono tracking-wider"
                              />
                            </div>
                          </div>

                          <button
                            type="submit"
                            disabled={isSubmittingLead}
                            className="w-full mt-2 py-3 px-4 rounded-xl btn-gradient text-white font-bold text-xs sm:text-sm transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 disabled:opacity-70"
                          >
                            {isSubmittingLead ? (
                              <span>Processing...</span>
                            ) : (
                              <>
                                <span>Get Free Demo &amp; Instant Pricing</span>
                                <ArrowRight size={15} />
                              </>
                            )}
                          </button>
                        </form>

                        <div className="mt-3 text-center text-[10px] text-slate-400 flex items-center justify-center gap-1.5 font-mono">
                          <Lock size={10} className="text-emerald-400" />
                          <span>100% Confidential · Zero Spam Guarantee</span>
                        </div>
                      </motion.div>
                    )}

                    {/* Step 3: Success Confirmation Screen */}
                    {wizardStep === 3 && (
                      <motion.div
                        key="step-3"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="mt-3.5 p-5 rounded-2xl bg-[#011432]/95 border border-emerald-500/40 backdrop-blur-md shadow-2xl text-center"
                      >
                        <div className="w-12 h-12 rounded-full bg-emerald-500/20 border border-emerald-400/50 flex items-center justify-center mx-auto mb-3 text-emerald-400">
                          <CheckCircle2 size={24} />
                        </div>
                        <h4 className="font-display font-bold text-base text-white">
                          Demo Access Requested!
                        </h4>
                        <p className="text-xs text-slate-300 mt-1.5 leading-relaxed">
                          Thank you, <strong className="text-white">{leadData.name}</strong>! Our edtech architects will share your customized <strong className="text-[#91E0F1]">{currentRole.badge}</strong> app demo &amp; pricing at <strong className="text-white font-mono">+91 {leadData.phone}</strong> shortly.
                        </p>

                        <div className="mt-4 pt-3 border-t border-white/10 flex flex-col gap-2">
                          <a
                            href={`https://wa.me/919876543210?text=Hi%20The%20Aurge%20Labs%20Team%2C%20I%20am%20${encodeURIComponent(leadData.name)}%20and%20I%20want%20a%20demo%20for%20${encodeURIComponent(currentRole.badge)}.`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full py-2.5 px-4 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-xs transition-all flex items-center justify-center gap-2 shadow-md"
                          >
                            <MessageSquare size={14} />
                            <span>Chat on WhatsApp Instantly</span>
                          </a>
                          
                          <button
                            type="button"
                            onClick={() => {
                              setWizardStep(1);
                              setLeadData({ name: "", phone: "" });
                            }}
                            className="text-[11px] text-slate-400 hover:text-white mt-1 transition-colors"
                          >
                            Submit another request
                          </button>
                        </div>
                      </motion.div>
                    )}

                  </div>
                </div>

              </Reveal>
            </div>

          </div>
        </div>
      </section>

      {/* SIMPLIFY YOUR TEACHING - BEST FEATURES SECTION */}
      <section className="py-20 sm:py-24 lg:py-28 px-5 sm:px-8 bg-white" data-testid="lms-best-features-section">
        <div className="max-w-5xl mx-auto text-center">
          <Reveal>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-[44px] text-[#01193D] tracking-tight leading-[1.2]">
              Simplify your teaching
              <br />
              with our best features
            </h2>
          </Reveal>

          {/* 6 Core Features Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-y-12 sm:gap-y-16 gap-x-6 sm:gap-x-12 mt-14 sm:mt-16 max-w-4xl mx-auto">
            {BEST_FEATURES.map((item, idx) => (
              <Reveal
                key={item.title}
                delay={idx * 0.06}
                className="flex flex-col items-center text-center group cursor-pointer"
              >
                <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                  <item.Icon />
                </div>
                <h3 className="font-display font-bold text-base sm:text-lg lg:text-[19px] text-[#01193D] mt-4 leading-tight group-hover:text-[#00AEEF] transition-colors">
                  {item.line1}
                  <br />
                  {item.line2}
                </h3>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LAUNCH YOUR BRANDED APP CALLOUT BANNER */}
      <section className="bg-[#E5F6FD] overflow-hidden py-12 lg:py-0 px-5 sm:px-8 relative" data-testid="lms-launch-app-banner">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center min-h-[380px] lg:min-h-[420px]">
            
            {/* Left Column: Phone Illustration */}
            <div className="lg:col-span-5 flex justify-center lg:justify-start items-end pt-4 lg:pt-8">
              <Reveal delay={0.1} className="w-full flex justify-center lg:justify-start">
                <img
                  src="/yourapp.png"
                  alt="Your Branded App"
                  className="max-h-[340px] sm:max-h-[400px] lg:max-h-[440px] w-auto object-contain object-bottom -mb-2 lg:-mb-4 hover:scale-[1.02] transition-transform duration-300 drop-shadow-md"
                />
              </Reveal>
            </div>

            {/* Right Column: Title & CTA Button */}
            <div className="lg:col-span-7 flex flex-col items-center lg:items-start text-center lg:text-left py-6 lg:py-16">
              <Reveal delay={0.2}>
                <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-[44px] xl:text-[48px] text-[#01193D] tracking-tight leading-[1.18]">
                  Are you excited to launch
                  <br />
                  your branded app?
                </h2>
                <div className="mt-7">
                  <Link
                    to="/contact"
                    data-testid="lms-launch-app-cta"
                    className="inline-flex items-center gap-2.5 bg-[#01193D] hover:bg-[#2563EB] text-white text-sm font-semibold px-8 py-3.5 rounded-full transition-all duration-200 shadow-[0_8px_24px_rgba(1,25,61,0.2)] hover:shadow-[0_12px_28px_rgba(37,99,235,0.35)] hover:-translate-y-0.5 group"
                  >
                    <span>Click here</span>
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </Reveal>
            </div>

          </div>
        </div>
      </section>

      {/* 10X GROWTH WEBINAR CALLOUT CARD */}
      <section className="py-12 sm:py-16 px-5 sm:px-8 bg-white" data-testid="lms-webinar-section">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="rounded-[32px] overflow-hidden bg-[#7EE1F7] relative shadow-[0_12px_40px_rgba(9,178,237,0.12)] border border-[#91E0F1]/40">
              <div className="grid lg:grid-cols-12 items-center">
                
                {/* Left Column: Heading, Subtitle & Watch Now Button */}
                <div className="lg:col-span-6 p-8 sm:p-12 lg:p-16 z-10 flex flex-col items-center lg:items-start text-center lg:text-left">
                  <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-[44px] xl:text-[48px] text-[#01193D] tracking-tight leading-[1.15]">
                    Attend the 10x growth <br />
                    webinar now!
                  </h2>
                  <p className="text-[#01193D]/80 text-base sm:text-lg lg:text-xl font-normal leading-relaxed mt-4 max-w-md">
                    Learn how teachers, educators and content creators are growing their business online.
                  </p>
                  <div className="mt-8">
                    <Link
                      to="/contact"
                      data-testid="lms-webinar-cta"
                      className="inline-flex items-center gap-2.5 bg-[#01193D] hover:bg-[#2563EB] text-white text-sm font-semibold px-8 py-3.5 rounded-full transition-all duration-200 shadow-[0_8px_24px_rgba(1,25,61,0.25)] hover:shadow-[0_12px_28px_rgba(37,99,235,0.35)] hover:-translate-y-0.5 group"
                    >
                      <span>Book Free Consultation</span>
                      <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>

                {/* Right Column: 3D Illustrative Team Artwork */}
                <div className="lg:col-span-6 flex justify-center lg:justify-end items-end relative self-stretch overflow-hidden">
                  <div className="relative w-full h-full min-h-[300px] sm:min-h-[360px] lg:min-h-[420px] flex items-end justify-center lg:justify-end">
                    <img
                      src="/lms-webinar-team.jpg"
                      alt="The Aurge Labs Mentors & Educator Team"
                      className="w-full h-full object-cover object-center lg:object-right-bottom scale-100 hover:scale-[1.02] transition-transform duration-500"
                    />
                    {/* Left soft horizontal fade gradient to blend with card text side */}
                    <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#7EE1F7] to-transparent hidden lg:block pointer-events-none" />
                  </div>
                </div>

              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 1. THE CORE 3-TIER "LIFETIME OWNERSHIP" PRICING CARDS */}
      <section id="pricing" className="py-20 lg:py-28 px-5 sm:px-8 bg-[#F8FAFC] relative overflow-hidden scroll-mt-24" data-testid="lms-lifetime-pricing-section">
        <div className="max-w-7xl mx-auto">
          
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto">
            <Reveal>
              <div className="flex items-center justify-center gap-2 mb-3.5">
                <span className="font-mono-brand inline-flex items-center gap-2 bg-[#CAF2F9]/70 border border-[#91E0F1]/60 text-[#2563EB] text-[11px] font-semibold tracking-[0.16em] uppercase px-3.5 py-1.5 rounded-full">
                  <Sparkles size={13} className="text-[#09B2ED]" /> Lifetime License Model
                </span>
              </div>
              <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-[44px] text-[#01193D] tracking-tight leading-[1.18]">
                Pay Once. Own Your Teaching <br className="hidden sm:inline" />
                Platform for Life.
              </h2>
              <p className="text-[#475569] text-base lg:text-lg mt-4 leading-relaxed max-w-2xl mx-auto font-normal">
                No monthly software subscription fees. Pay once for complete setup and own your website and mobile app with 1 year free hosting.
              </p>
            </Reveal>
          </div>

          {/* 3 Lifetime Pricing Cards */}
          <div className="grid lg:grid-cols-3 gap-8 mt-14 lg:mt-16 items-stretch">
            {LIFETIME_PRICING_PLANS.map((plan, idx) => (
              <Reveal
                key={plan.id}
                delay={idx * 0.1}
                className="h-full flex flex-col"
              >
                <div
                  className={`relative h-full rounded-3xl p-7 sm:p-8 transition-all duration-300 flex flex-col justify-between ${
                    plan.popular
                      ? "bg-white border-2 border-[#09B2ED] shadow-[0_20px_50px_rgba(9,178,237,0.18)] ring-4 ring-[#09B2ED]/10 -translate-y-2"
                      : "bg-white border border-slate-200/90 soft-shadow hover:border-slate-300"
                  }`}
                >
                  {/* Popular Badge */}
                  {plan.popular && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                      <span className="bg-gradient-to-r from-[#2563EB] to-[#09B2ED] text-white text-[11px] font-bold tracking-wider uppercase px-4 py-1 rounded-full shadow-md">
                        ★ Most Popular
                      </span>
                    </div>
                  )}

                  <div className="flex flex-col flex-grow">
                    {/* Minimal Icon Box + Target Badge */}
                    <div className="flex items-center justify-between min-h-[42px]">
                      <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${
                        plan.popular 
                          ? "bg-gradient-to-br from-[#2563EB]/10 to-[#09B2ED]/20 border border-[#09B2ED]/30 text-[#2563EB]" 
                          : "bg-slate-100/80 border border-slate-200/80 text-[#2563EB]"
                      }`}>
                        <plan.Icon size={20} className={plan.popular ? "text-[#09B2ED]" : "text-[#2563EB]"} />
                      </div>
                      <span className="font-mono text-[10px] font-semibold text-[#2563EB] bg-[#CAF2F9]/70 border border-[#91E0F1]/50 px-3 py-1 rounded-full whitespace-nowrap">
                        {plan.badge}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="font-display font-bold text-2xl text-[#01193D] mt-4 tracking-tight">
                      {plan.name}
                    </h3>

                    {/* Tagline */}
                    <p className="text-xs text-slate-500 font-normal mt-1.5 min-h-[34px] leading-relaxed">
                      {plan.tagline}
                    </p>

                    {/* Price */}
                    <div className="mt-4 pt-4 border-t border-slate-100 min-h-[72px] flex flex-col justify-center">
                      <div className="flex items-baseline gap-1.5">
                        <span className="font-display font-extrabold text-4xl sm:text-5xl text-[#01193D] tracking-tight">
                          ₹{plan.price.toLocaleString("en-IN")}
                        </span>
                      </div>
                      <div className="text-[11px] text-emerald-600 font-mono font-semibold mt-1">
                        {plan.priceLabel}
                      </div>
                    </div>

                    {/* Minimal Included Platforms Box */}
                    <div className="mt-4 p-3.5 rounded-2xl bg-[#F8FAFC] border border-slate-200/90 text-xs min-h-[82px] flex flex-col justify-center">
                      <div className="text-[10px] font-mono uppercase font-bold text-slate-400 tracking-wider mb-2 flex items-center gap-1.5">
                        <Sparkles size={11} className="text-[#09B2ED]" /> Included Platforms:
                      </div>
                      <div className="space-y-1.5">
                        {plan.appsIncluded.map(({ Icon: PlatformIcon, label }) => (
                          <div key={label} className="flex items-center gap-2 font-medium text-[#01193D]">
                            <PlatformIcon size={14} className="text-[#2563EB] shrink-0" />
                            <span className="leading-tight text-xs font-semibold">{label}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Core LMS Features Header & List */}
                    <div className="mt-5 pt-4 border-t border-slate-100 flex-grow">
                      <div className="font-mono text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2.5 min-h-[20px] flex items-center">
                        {plan.extraIncluded || "Core Features Included:"}
                      </div>
                      <div className="space-y-2.5 min-h-[175px]">
                        {plan.coreFeatures.map((feat) => (
                          <div key={feat} className="flex items-start gap-2.5 text-xs sm:text-sm text-[#01193D] font-medium leading-tight">
                            <CheckCircle2 size={15} className="text-[#09B2ED] shrink-0 mt-0.5" />
                            <span>{feat}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Year 1 Free Allowances */}
                    <div className="mt-5 pt-4 border-t border-slate-100 min-h-[92px]">
                      <div className="font-mono text-[11px] uppercase tracking-wider text-emerald-600 font-bold mb-2">
                        Year 1 Bonus Allowances (100% Free):
                      </div>
                      <div className="space-y-1.5">
                        {plan.year1Bonuses.map((bonus) => (
                          <div key={bonus} className="text-xs text-slate-700 font-medium flex items-start gap-2 leading-tight">
                            <span className="text-emerald-500 font-bold">✓</span>
                            <span>{bonus}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Support & Consultation */}
                    <div className="mt-4 pt-3.5 border-t border-slate-100 min-h-[74px] flex flex-col justify-center space-y-1.5 text-xs text-slate-600">
                      <div className="leading-tight">
                        <strong className="text-[#01193D]">Support:</strong> {plan.support}
                      </div>
                      {plan.consultation ? (
                        <div className="leading-tight">
                          <strong className="text-[#01193D]">Consultation:</strong> {plan.consultation}
                        </div>
                      ) : (
                        <div className="leading-tight text-slate-400">
                          <strong className="text-slate-400">Consultation:</strong> Available as add-on
                        </div>
                      )}
                    </div>
                  </div>

                  {/* CTA Button */}
                  <div className="mt-6 pt-5 border-t border-slate-100">
                    <Link
                      to="/contact"
                      data-testid={`lifetime-pricing-${plan.id}-cta`}
                      className={`w-full py-3.5 px-6 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-2 transition-all duration-200 ${
                        plan.popular
                          ? "btn-gradient text-white shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40"
                          : "bg-[#01193D] hover:bg-[#2563EB] text-white shadow-md"
                      }`}
                    >
                      <span>{plan.cta}</span>
                      <ArrowRight size={15} />
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* 2. TRANSPARENT USAGE-BASED COSTS SECTION */}
          <div className="mt-20 pt-16 border-t border-slate-200" data-testid="lms-usage-costs-section">
            <Reveal className="text-center max-w-3xl mx-auto mb-12">
              <span className="font-mono-brand inline-flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-[11px] font-semibold tracking-[0.16em] uppercase px-3.5 py-1.5 rounded-full mb-3">
                <ShieldAlert size={14} className="text-amber-600" /> Usage-Based Transparency
              </span>
              <h3 className="font-display font-bold text-2xl sm:text-3xl text-[#01193D] tracking-tight">
                Transparent Usage-Based Costs
              </h3>
              <p className="text-sm sm:text-base text-slate-600 mt-2.5">
                Clear breakdown of what happens if you exceed allowances in Year 1 or enter Year 2. Zero hidden fees.
              </p>
            </Reveal>

            <div className="grid md:grid-cols-3 gap-6 items-stretch">
              {USAGE_COSTS.map((item, idx) => (
                <Reveal
                  key={item.title}
                  delay={idx * 0.08}
                  className="h-full flex flex-col"
                >
                  <div className="h-full bg-white rounded-3xl overflow-hidden border border-slate-200/90 soft-shadow flex flex-col justify-between hover:border-slate-300 transition-all group">
                    
                    {/* Row 1: Image Full Cover Area (Adaptive 1:1 Aspect Ratio to prevent cropping) */}
                    <div className="relative w-full aspect-square overflow-hidden border-b border-slate-100 bg-white">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover scale-100 group-hover:scale-105 transition-transform duration-500"
                      />
                      {/* Overlay Badge in Top Corner */}
                      <div className="absolute top-4 right-4 z-10">
                        <span className="font-mono text-[10px] font-semibold text-slate-700 bg-white/95 backdrop-blur-sm border border-slate-200/90 px-3 py-1 rounded-full shadow-sm">
                          {item.badge}
                        </span>
                      </div>
                    </div>

                    {/* Row 2: Text Content */}
                    <div className="p-6 sm:p-7 flex flex-col justify-between flex-grow">
                      <div>
                        <h4 className="font-display font-bold text-xl text-[#01193D] tracking-tight">
                          {item.title}
                        </h4>
                        <div className="inline-flex items-center mt-2.5 px-3 py-1 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 font-mono text-xs font-bold">
                          {item.highlight}
                        </div>
                        <p className="text-xs sm:text-[13px] text-slate-600 leading-relaxed mt-3.5">
                          {item.desc}
                        </p>
                      </div>
                    </div>

                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* Note Below Pricing & Usage Section */}
          <Reveal delay={0.15} className="mt-12 text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2.5 px-6 py-3 rounded-full bg-[#CAF2F9]/50 border border-[#91E0F1]/70 text-xs sm:text-sm font-medium text-[#01193D] shadow-sm">
              <Sparkles size={16} className="text-[#2563EB] shrink-0" />
              <span>
                <strong>Note:</strong> Pay once and own your complete website &amp; app for life with 0% monthly SaaS platform fees.
              </span>
            </div>
          </Reveal>

        </div>
      </section>

      {/* ARE YOU READY TO START YOUR GROWTH JOURNEY CALLOUT CARD */}
      <section className="py-12 sm:py-16 px-5 sm:px-8 bg-white" data-testid="lms-growth-journey-section">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="rounded-[32px] overflow-hidden bg-[#D4F2FB] relative shadow-[0_12px_40px_rgba(9,178,237,0.12)] border border-[#A6E6F7]/50">
              <div className="grid lg:grid-cols-2 items-stretch min-h-[380px] lg:min-h-[440px]">
                
                {/* Column 1: Image Fully Covering the Left Column */}
                <div className="relative w-full h-[280px] sm:h-[360px] lg:h-full overflow-hidden">
                  <img
                    src="/growth-journey-hand.jpg"
                    alt="Start Your Growth Journey"
                    className="w-full h-full object-cover object-center scale-100 hover:scale-105 transition-transform duration-700"
                  />
                </div>

                {/* Column 2: Text & CTA */}
                <div className="p-6 sm:p-10 lg:p-12 xl:p-16 flex flex-col justify-center items-start text-left z-10">
                  <h2 className="font-display font-bold text-2xl sm:text-3xl md:text-4xl lg:text-[36px] xl:text-[42px] text-[#01193D] tracking-tight leading-snug sm:leading-tight">
                    Are you ready to start <br className="hidden sm:inline" />
                    your growth journey?
                  </h2>
                  <div className="mt-6 sm:mt-8">
                    <Link
                      to="/contact"
                      data-testid="growth-journey-cta"
                      className="inline-flex items-center gap-2.5 bg-[#01193D] hover:bg-[#2563EB] text-white text-sm sm:text-base font-semibold px-7 sm:px-8 py-3.5 rounded-full transition-all duration-200 shadow-[0_8px_24px_rgba(1,25,61,0.25)] hover:shadow-[0_12px_28px_rgba(37,99,235,0.35)] hover:-translate-y-0.5 group"
                    >
                      <span>Start now</span>
                      <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>

              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section id="faq" className="py-20 lg:py-28 px-5 sm:px-8 bg-[#F8FAFC] scroll-mt-24" data-testid="lms-faq-section">
        <div className="max-w-4xl mx-auto">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <span className="font-mono-brand inline-flex items-center gap-2 bg-[#CAF2F9]/70 border border-[#91E0F1]/60 text-[#2563EB] text-[11px] font-semibold tracking-[0.16em] uppercase px-3.5 py-1.5 rounded-full mb-3">
              <HelpCircle size={14} className="text-[#09B2ED]" /> Got Questions?
            </span>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-[42px] text-[#01193D] tracking-tight leading-[1.18]">
              Frequently Asked Questions
            </h2>
            <p className="text-[#475569] text-base mt-3.5 leading-relaxed">
              Everything you need to know about our Lifetime Ownership model, security, and app setup.
            </p>
          </Reveal>

          <div className="space-y-4">
            {LMS_FAQS.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <Reveal key={faq.q} delay={idx * 0.04}>
                  <div
                    className={`rounded-2xl border transition-all duration-200 overflow-hidden bg-white ${
                      isOpen
                        ? "border-[#09B2ED]/60 shadow-[0_8px_30px_rgba(9,178,237,0.08)] ring-2 ring-[#09B2ED]/10"
                        : "border-slate-200/80 hover:border-slate-300"
                    }`}
                  >
                    <button
                      onClick={() => setOpenFaq(isOpen ? null : idx)}
                      className="w-full text-left p-5 sm:p-6 flex items-center justify-between gap-4 focus:outline-none"
                    >
                      <span className="font-display font-bold text-base sm:text-lg text-[#01193D] leading-snug">
                        {faq.q}
                      </span>
                      <span
                        className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-transform duration-300 ${
                          isOpen ? "bg-[#09B2ED] text-white rotate-180" : "bg-slate-100 text-[#01193D]"
                        }`}
                      >
                        <ChevronDown size={18} />
                      </span>
                    </button>

                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25, ease: "easeInOut" }}
                        >
                          <div className="px-5 sm:px-6 pb-6 pt-1 text-sm sm:text-base text-slate-600 leading-relaxed border-t border-slate-100">
                            {faq.a}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Quick Help Box Below FAQ */}
          <Reveal delay={0.2} className="mt-12 text-center">
            <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200/90 soft-shadow flex flex-col sm:flex-row items-center justify-between gap-6">
              <div className="text-left">
                <h4 className="font-display font-bold text-lg text-[#01193D]">
                  Have a question not answered here?
                </h4>
                <p className="text-xs sm:text-sm text-slate-600 mt-1">
                  Our edtech architects are happy to walk you through everything on a live call.
                </p>
              </div>
              <Link
                to="/contact"
                className="bg-[#01193D] hover:bg-[#2563EB] text-white text-xs sm:text-sm font-semibold px-6 py-3 rounded-full shrink-0 inline-flex items-center gap-2 shadow-md transition-all"
              >
                <span>Book Free Consultation</span>
                <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FINAL BOTTOM CTA */}
      <CTABanner
        title="Ready to own your teaching platform?"
        subtitle="Get a complete branded website and Android app with 0% platform commissions for life."
        cta="Book Free Consultation"
      />
    </div>
  );
}
